/* Slice 2H · Measurement Intelligence Foundation — Work Areas.
 *
 * Inline card mounted at the top of ProjectDetail. Contractor enters
 * structured measurements manually. Deterministic calculations are
 * performed server-side. Approved records survive reload. Editing an
 * approved record returns it to review-required status.
 *
 * Non-negotiable contract:
 *   - Never mutates estimates, proposals, contracts, invoices, pricing,
 *     schedules, or lifecycle status. Only reads and writes rows in the
 *     work_areas collection through the /api/projects/{pid}/work-areas
 *     endpoints added by Slice 2H.
 *   - Every capture source runs through the same review / approval /
 *     storage workflow. This slice ships ``manual`` only.
 *   - Every contractor-facing string uses the existing EN/ES i18n
 *     framework (useLang().t).
 *   - Mobile-first: flex-col sm:flex-row card idiom used throughout.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import { Ruler, Plus, X, CheckCircle2, AlertTriangle, Pencil, Loader2, Trash2 } from "lucide-react";
import LeviCandidateReviewSection from "./LeviCandidateReviewSection";

const AREA_TYPES = [
  "interior_room", "exterior_area", "roof_section", "elevation",
  "commercial_space", "mechanical_area", "linear_run", "object", "custom",
];
const RECT_TYPES = new Set(["interior_room", "commercial_space", "mechanical_area"]);
const OPENING_TYPES = ["door", "window", "opening", "other"];

const emptyDraft = () => ({
  name: "",
  area_type: "interior_room",
  measurement_mode: "rectangular",
  source: "manual",
  description: "",
  length: "",
  width: "",
  height: "",
  total_length: "",
  count: "",
  openings: [],
  custom_dimensions: [],
  notes: "",
});

const numOrNull = (s) => {
  if (s === "" || s === null || s === undefined) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const toPayload = (d) => ({
  name: (d.name || "").trim(),
  area_type: d.area_type,
  measurement_mode: d.measurement_mode,
  source: "manual",
  description: d.description || "",
  length: numOrNull(d.length),
  width: numOrNull(d.width),
  height: numOrNull(d.height),
  total_length: numOrNull(d.total_length),
  count: d.count === "" ? null : parseInt(d.count, 10) || null,
  openings: (d.openings || []).map((o) => ({
    opening_type: o.opening_type,
    label: o.label || "",
    width: Number(o.width) || 0,
    height: Number(o.height) || 0,
    quantity: parseInt(o.quantity, 10) || 0,
  })),
  custom_dimensions: (d.custom_dimensions || []).map((c) => ({
    label: (c.label || "").trim(),
    value: Number(c.value) || 0,
    unit: c.unit || "ft",
    notes: c.notes || "",
  })),
  notes: d.notes || "",
});

const fromRecord = (rec) => ({
  name: rec.name || "",
  area_type: rec.area_type || "interior_room",
  measurement_mode: rec.measurement_mode || "rectangular",
  source: rec.source || "manual",
  description: rec.description || "",
  length: rec.length ?? "",
  width: rec.width ?? "",
  height: rec.height ?? "",
  total_length: rec.total_length ?? "",
  count: rec.count ?? "",
  openings: (rec.openings || []).map((o) => ({ ...o })),
  custom_dimensions: (rec.custom_dimensions || []).map((c) => ({ ...c })),
  notes: rec.notes || "",
});

const fmt = (v) => (v === null || v === undefined ? "" : (Math.round(v * 100) / 100).toString());

const PLACEHOLDER_KEY_BY_AREA_TYPE = {
  interior_room:   "interior_room",
  exterior_area:   "exterior_area",
  roof_section:    "roof_section",
  elevation:       "elevation",
  object:          "object",
  linear_run:      "linear_measurement",
  custom:          "something_else",
  // Fallback for the two additional persisted types (commercial_space,
  // mechanical_area) which are not exposed by the chooser but may exist
  // on already-saved records — reuse a sensible interior placeholder.
  commercial_space: "interior_room",
  mechanical_area:  "interior_room",
};

const CalcRow = ({ label, value, unit, testid }) => (
  value === null || value === undefined ? null : (
    <div className="flex items-center justify-between py-1 text-sm" data-testid={testid}>
      <span className="text-[var(--jc-text-soft)]">{label}</span>
      <span className="font-semibold text-[var(--jc-text)]">{fmt(value)} <span className="text-xs text-[var(--jc-text-soft)]">{unit}</span></span>
    </div>
  )
);

// Slice 2H final polish · Saved-card compact metric cell. Label sits
// directly above its value so contractors can pair them at a glance
// without scanning a wide row. Skipped entirely when there is no
// value — preserves the existing null-safe behavior used by CalcRow.
const stripUnitSuffix = (s) => (typeof s === "string" ? s.replace(/\s*\([^)]*\)\s*$/, "") : s);
const MetricCell = ({ label, value, unit, testid }) => (
  value === null || value === undefined ? null : (
    <div
      data-testid={testid}
      className="flex flex-col gap-0.5 rounded-md border border-[var(--jc-border)] bg-[var(--jc-bg)] px-3 py-2"
    >
      <span className="text-xs text-[var(--jc-text-soft)] leading-tight">{stripUnitSuffix(label)}</span>
      <span className="text-sm font-semibold text-[var(--jc-text)] leading-tight">
        {fmt(value)} <span className="text-xs font-normal text-[var(--jc-text-soft)]">{unit}</span>
      </span>
    </div>
  )
);

const WorkAreaEditor = ({ t, initial, onCancel, onSave, saving, mode, onChangeType }) => {
  const [d, setD] = useState(initial);
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const placeholderKey = PLACEHOLDER_KEY_BY_AREA_TYPE[d.area_type] || "something_else";
  const namePlaceholder = t(`work_areas.form.name_placeholders.${placeholderKey}`);
  const addOpening = () => set("openings", [...(d.openings || []), { opening_type: "door", label: "", width: "", height: "", quantity: 1 }]);
  const removeOpening = (i) => set("openings", (d.openings || []).filter((_, idx) => idx !== i));
  const editOpening = (i, patch) => set("openings", (d.openings || []).map((o, idx) => idx === i ? { ...o, ...patch } : o));
  const addCustom = () => set("custom_dimensions", [...(d.custom_dimensions || []), { label: "", value: "", unit: "ft", notes: "" }]);
  const removeCustom = (i) => set("custom_dimensions", (d.custom_dimensions || []).filter((_, idx) => idx !== i));
  const editCustom = (i, patch) => set("custom_dimensions", (d.custom_dimensions || []).map((c, idx) => idx === i ? { ...c, ...patch } : c));

  // Client-side deterministic preview (mirrors backend _calc). Server
  // remains the source of truth on save.
  const preview = useMemo(() => {
    const L = numOrNull(d.length), W = numOrNull(d.width), H = numOrNull(d.height);
    const TL = numOrNull(d.total_length);
    const calc = {};
    if (d.measurement_mode === "rectangular" && L !== null && W !== null) {
      calc.floor_area = L * W;
      calc.ceiling_area = L * W;
      calc.perimeter = 2 * L + 2 * W;
      if (H !== null) {
        const gross = 2 * L * H + 2 * W * H;
        calc.gross_wall_area = gross;
        const totalOp = (d.openings || []).reduce(
          (s, o) => s + (Number(o.width) || 0) * (Number(o.height) || 0) * (parseInt(o.quantity, 10) || 0), 0);
        calc.total_opening_area = totalOp;
        calc.net_wall_area = gross - totalOp;
      }
    } else if (d.measurement_mode === "linear" && TL !== null) {
      calc.linear_feet = TL;
      if (H !== null) calc.run_wall_area = TL * H;
    }
    return calc;
  }, [d]);

  const opWarn = d.measurement_mode === "rectangular"
    && preview.gross_wall_area !== undefined
    && preview.total_opening_area !== undefined
    && preview.total_opening_area > preview.gross_wall_area;

  return (
    <div data-testid="work-area-editor" className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-[var(--jc-text-soft)]">{t("work_areas.form.name")}</span>
          <input
            type="text"
            value={d.name}
            onChange={(e) => set("name", e.target.value)}
            data-testid="work-area-name-input"
            className="jc-input"
            placeholder={namePlaceholder}
          />
        </label>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-[var(--jc-text-soft)]">{t("work_areas.form.area_type")}</span>
          <div
            data-testid="work-area-selected-type"
            className="flex items-center justify-between jc-input bg-[var(--jc-bg)]"
          >
            <span className="text-sm font-semibold">{t(`work_areas.types.${d.area_type}`)}</span>
            {mode === "new" && onChangeType && (
              <button
                type="button"
                onClick={onChangeType}
                data-testid="work-area-change-type-btn"
                className="text-xs font-semibold text-[var(--jc-blue)] hover:underline"
              >
                {t("work_areas.form.change_type")}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-md bg-[var(--jc-bg)] p-3 text-xs text-[var(--jc-text-soft)] hidden" data-testid="work-area-source-note">
        {t("work_areas.form.source_manual")}
      </div>

      {d.measurement_mode === "rectangular" && (
        <div data-testid="work-area-rectangular-fields" className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-[var(--jc-text-soft)]">{t("work_areas.form.length_ft")}</span>
              <input type="number" min="0" step="0.01" value={d.length} onChange={(e) => set("length", e.target.value)} className="jc-input" data-testid="work-area-length-input" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-[var(--jc-text-soft)]">{t("work_areas.form.width_ft")}</span>
              <input type="number" min="0" step="0.01" value={d.width} onChange={(e) => set("width", e.target.value)} className="jc-input" data-testid="work-area-width-input" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-[var(--jc-text-soft)]">{t("work_areas.form.height_ft")}</span>
              <input type="number" min="0" step="0.01" value={d.height} onChange={(e) => set("height", e.target.value)} className="jc-input" data-testid="work-area-height-input" />
            </label>
          </div>

          <div data-testid="work-area-openings-section" className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-[var(--jc-text-soft)]">{t("work_areas.form.openings")}</span>
              <button type="button" onClick={addOpening} data-testid="work-area-add-opening-btn" className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--jc-blue)] hover:underline">
                <Plus size={12} /> {t("work_areas.form.add_opening")}
              </button>
            </div>
            {(d.openings || []).map((o, i) => (
              <div key={i} data-testid={`work-area-opening-row-${i}`} className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
                <select value={o.opening_type} onChange={(e) => editOpening(i, { opening_type: e.target.value })} data-testid={`work-area-opening-type-${i}`} className="jc-input col-span-2 sm:col-span-1">
                  {OPENING_TYPES.map((v) => <option key={v} value={v}>{t(`work_areas.openings.${v}`)}</option>)}
                </select>
                <input type="text" value={o.label} onChange={(e) => editOpening(i, { label: e.target.value })} placeholder={t("work_areas.form.opening_label")} className="jc-input" />
                <input type="number" min="0" step="0.01" value={o.width} onChange={(e) => editOpening(i, { width: e.target.value })} placeholder={t("work_areas.form.opening_width")} className="jc-input" data-testid={`work-area-opening-width-${i}`} />
                <input type="number" min="0" step="0.01" value={o.height} onChange={(e) => editOpening(i, { height: e.target.value })} placeholder={t("work_areas.form.opening_height")} className="jc-input" data-testid={`work-area-opening-height-${i}`} />
                <div className="flex items-center gap-1">
                  <input type="number" min="0" step="1" value={o.quantity} onChange={(e) => editOpening(i, { quantity: e.target.value })} placeholder={t("work_areas.form.opening_qty")} className="jc-input" data-testid={`work-area-opening-qty-${i}`} />
                  <button type="button" onClick={() => removeOpening(i)} data-testid={`work-area-remove-opening-${i}`} className="p-1 text-[var(--jc-red)] hover:bg-[var(--jc-red)]/10 rounded">
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {d.measurement_mode === "linear" && (
        <div data-testid="work-area-linear-fields" className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-[var(--jc-text-soft)]">{t("work_areas.form.total_length_ft")}</span>
            <input type="number" min="0" step="0.01" value={d.total_length} onChange={(e) => set("total_length", e.target.value)} className="jc-input" data-testid="work-area-total-length-input" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-[var(--jc-text-soft)]">{t("work_areas.form.height_ft")}</span>
            <input type="number" min="0" step="0.01" value={d.height} onChange={(e) => set("height", e.target.value)} className="jc-input" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-[var(--jc-text-soft)]">{t("work_areas.form.count")}</span>
            <input type="number" min="0" step="1" value={d.count} onChange={(e) => set("count", e.target.value)} className="jc-input" data-testid="work-area-count-input" />
          </label>
        </div>
      )}

      {d.measurement_mode === "custom_labeled" && (
        <div data-testid="work-area-custom-fields" className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-[var(--jc-text-soft)]">{t("work_areas.form.custom_dimensions")}</span>
            <button type="button" onClick={addCustom} data-testid="work-area-add-custom-btn" className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--jc-blue)] hover:underline">
              <Plus size={12} /> {t("work_areas.form.add_custom")}
            </button>
          </div>
          {(d.custom_dimensions || []).map((c, i) => (
            <div key={i} data-testid={`work-area-custom-row-${i}`} className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
              <input type="text" value={c.label} onChange={(e) => editCustom(i, { label: e.target.value })} placeholder={t("work_areas.form.custom_label_placeholder")} className="jc-input col-span-2" data-testid={`work-area-custom-label-${i}`} />
              <input type="number" step="0.01" value={c.value} onChange={(e) => editCustom(i, { value: e.target.value })} className="jc-input" data-testid={`work-area-custom-value-${i}`} />
              <select value={c.unit} onChange={(e) => editCustom(i, { unit: e.target.value })} className="jc-input">
                <option value="ft">{t("work_areas.units.ft")}</option>
                <option value="in">{t("work_areas.units.in")}</option>
                <option value="linear_ft">{t("work_areas.units.linear_ft")}</option>
                <option value="sqft">{t("work_areas.units.sqft")}</option>
                <option value="count">{t("work_areas.units.count")}</option>
              </select>
              <button type="button" onClick={() => removeCustom(i)} data-testid={`work-area-remove-custom-${i}`} className="p-1 text-[var(--jc-red)] hover:bg-[var(--jc-red)]/10 rounded self-start">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <label className="flex flex-col gap-1">
        <span className="text-xs font-semibold text-[var(--jc-text-soft)]">{t("work_areas.form.notes")}</span>
        <textarea rows={2} value={d.notes} onChange={(e) => set("notes", e.target.value)} className="jc-input" data-testid="work-area-notes-input" />
      </label>

      {(preview.floor_area !== undefined || preview.linear_feet !== undefined) && (
        <div data-testid="work-area-calc-preview" className="rounded-md border border-[var(--jc-border)] p-3 bg-[var(--jc-bg)]">
          <div className="text-xs font-semibold text-[var(--jc-text-soft)] mb-1">{t("work_areas.calc.preview_title")}</div>
          <CalcRow label={t("work_areas.calc.floor_area")} value={preview.floor_area} unit="sqft" testid="calc-floor-area" />
          <CalcRow label={t("work_areas.calc.ceiling_area")} value={preview.ceiling_area} unit="sqft" testid="calc-ceiling-area" />
          <CalcRow label={t("work_areas.calc.perimeter")} value={preview.perimeter} unit="ft" testid="calc-perimeter" />
          <CalcRow label={t("work_areas.calc.gross_wall_area")} value={preview.gross_wall_area} unit="sqft" testid="calc-gross-wall-area" />
          <CalcRow label={t("work_areas.calc.total_opening_area")} value={preview.total_opening_area} unit="sqft" testid="calc-total-opening-area" />
          <CalcRow label={t("work_areas.calc.net_wall_area")} value={preview.net_wall_area} unit="sqft" testid="calc-net-wall-area" />
          <CalcRow label={t("work_areas.calc.linear_feet")} value={preview.linear_feet} unit="ft" testid="calc-linear-feet" />
          <CalcRow label={t("work_areas.calc.run_wall_area")} value={preview.run_wall_area} unit="sqft" testid="calc-run-wall-area" />
        </div>
      )}

      {opWarn && (
        <div data-testid="work-area-opening-overrun-warning" className="flex items-start gap-2 rounded-md bg-[var(--jc-red)]/10 border border-[var(--jc-red)]/30 p-3 text-sm text-[var(--jc-red)]">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{t("work_areas.validation.openings_exceed_walls")}</span>
        </div>
      )}

      <div className="rounded-md bg-[var(--jc-yellow)]/10 border border-[var(--jc-yellow)]/40 p-3 text-xs text-[var(--jc-text)]" data-testid="work-area-no-mutation-notice">
        {t("work_areas.form.no_mutation_notice")}
      </div>

      <div className="flex flex-col sm:flex-row sm:justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} disabled={saving} data-testid="work-area-cancel-btn" className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)]">
          {t("work_areas.form.cancel")}
        </button>
        <button
          type="button"
          onClick={() => onSave(toPayload(d))}
          disabled={saving || opWarn || !(d.name || "").trim()}
          data-testid="work-area-save-draft-btn"
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : null}
          {mode === "edit" ? t("work_areas.form.save_review") : t("work_areas.form.save_draft")}
        </button>
      </div>
    </div>
  );
};

const WorkAreaReviewCard = ({ t, rec, onEdit, onApprove, onDelete, approving, deleting }) => {
  const approved = rec.approval_status === "approved";
  const c = rec.calculated || {};
  return (
    <div data-testid={`work-area-row-${rec.id}`} className="jc-card p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-base" data-testid={`work-area-name-${rec.id}`}>{rec.name}</div>
            <span className="text-xs px-2 py-0.5 rounded bg-[var(--jc-bg)] text-[var(--jc-text-soft)]">
              {t(`work_areas.types.${rec.area_type}`)}
            </span>
            {approved ? (
              <span data-testid={`work-area-status-approved-${rec.id}`} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">
                <CheckCircle2 size={12} /> {t("work_areas.status.approved")}
              </span>
            ) : (
              <span data-testid={`work-area-status-review-${rec.id}`} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">
                {t("work_areas.status.review_required")}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <button onClick={() => onEdit(rec)} data-testid={`work-area-edit-btn-${rec.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md border border-[var(--jc-border-strong)]">
            <Pencil size={12} /> {t("work_areas.actions.edit")}
          </button>
          {!approved && (
            <button onClick={() => onApprove(rec)} disabled={approving} data-testid={`work-area-approve-btn-${rec.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md jc-btn-primary disabled:opacity-60">
              {approving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
              {t("work_areas.actions.approve_and_save")}
            </button>
          )}
          <button onClick={() => onDelete(rec)} disabled={deleting} data-testid={`work-area-delete-btn-${rec.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md border border-[var(--jc-border-strong)] text-[var(--jc-red)]">
            <Trash2 size={12} />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mt-2" data-testid={`work-area-summary-${rec.id}`}>
        <MetricCell label={t("work_areas.form.length_ft")}       value={rec.length}          unit={t("work_areas.units.ft")}   testid={`dim-length-${rec.id}`} />
        <MetricCell label={t("work_areas.form.width_ft")}        value={rec.width}           unit={t("work_areas.units.ft")}   testid={`dim-width-${rec.id}`} />
        <MetricCell label={t("work_areas.form.height_ft")}       value={rec.height}          unit={t("work_areas.units.ft")}   testid={`dim-height-${rec.id}`} />
        <MetricCell label={t("work_areas.form.total_length_ft")} value={rec.total_length}    unit={t("work_areas.units.ft")}   testid={`dim-total-length-${rec.id}`} />
        <MetricCell label={t("work_areas.calc.floor_area")}          value={c.floor_area}          unit={t("work_areas.units.sqft")} testid={`calc-floor-${rec.id}`} />
        <MetricCell label={t("work_areas.calc.perimeter")}           value={c.perimeter}           unit={t("work_areas.units.ft")}   testid={`calc-perim-${rec.id}`} />
        <MetricCell label={t("work_areas.calc.gross_wall_area")}     value={c.gross_wall_area}     unit={t("work_areas.units.sqft")} testid={`calc-gross-${rec.id}`} />
        <MetricCell label={t("work_areas.calc.total_opening_area")}  value={c.total_opening_area}  unit={t("work_areas.units.sqft")} testid={`calc-opening-${rec.id}`} />
        <MetricCell label={t("work_areas.calc.net_wall_area")}       value={c.net_wall_area}       unit={t("work_areas.units.sqft")} testid={`calc-net-${rec.id}`} />
        <MetricCell label={t("work_areas.calc.linear_feet")}         value={c.linear_feet}         unit={t("work_areas.units.ft")}   testid={`calc-linear-${rec.id}`} />
      </div>
      {(rec.custom_dimensions || []).length > 0 && (
        <div className="mt-2 text-sm space-y-1" data-testid={`work-area-custom-summary-${rec.id}`}>
          {(rec.custom_dimensions || []).map((c, i) => (
            <div key={i}><span className="text-[var(--jc-text-soft)]">{c.label}: </span>{fmt(c.value)} <span className="text-xs text-[var(--jc-text-soft)]">{t(`work_areas.units.${c.unit}`)}</span></div>
          ))}
        </div>
      )}
      {approved && rec.approved_at && (
        <div className="mt-2 text-xs text-emerald-700" data-testid={`work-area-approved-at-${rec.id}`}>
          {t("work_areas.form.approved_at_label")}: {new Date(rec.approved_at).toLocaleString()}
        </div>
      )}
    </div>
  );
};

const WorkAreas = ({ projectId }) => {
  const { t } = useLang();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // {mode: "new"|"edit", rec?, draft}
  const [choosing, setChoosing] = useState(false); // Slice 2H correction · Levi-guided chooser gate
  const [saving, setSaving] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const reqKeyRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/work-areas`);
      setRecords(Array.isArray(data) ? data : []);
    } catch {
      // Silent — the empty state is contractor-friendly.
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { if (projectId) load(); }, [projectId, load]);

  const startNew = () => {
    // Slice 2H correction · Open the Levi-guided chooser first — do NOT
    // create a backend record until the contractor saves. The chooser
    // reveals the appropriate form only after the contractor picks
    // what they want to measure.
    setChoosing(true);
    reqKeyRef.current = `wa-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  };
  const pickChooser = (choice) => {
    // Map contractor-facing chooser choice to the persisted area_type
    // + derived measurement_mode. No new area_types introduced.
    const map = {
      interior_room:       { area_type: "interior_room",   measurement_mode: "rectangular" },
      exterior_area:       { area_type: "exterior_area",   measurement_mode: "rectangular" },
      roof_section:        { area_type: "roof_section",    measurement_mode: "rectangular" },
      elevation:           { area_type: "elevation",       measurement_mode: "rectangular" },
      object:              { area_type: "object",          measurement_mode: "custom_labeled" },
      linear_measurement:  { area_type: "linear_run",      measurement_mode: "linear" },
      something_else:      { area_type: "custom",          measurement_mode: "custom_labeled" },
    };
    const chosen = map[choice] || map.interior_room;
    setEditing({ mode: "new", draft: { ...emptyDraft(), ...chosen } });
    setChoosing(false);
  };
  const cancelChoosing = () => setChoosing(false);
  const startEdit = (rec) => {
    setEditing({ mode: "edit", rec, draft: fromRecord(rec) });
    setChoosing(false);
  };
  const cancelEditing = () => setEditing(null);

  const save = async (payload) => {
    if (saving) return;
    setSaving(true);
    try {
      if (editing?.mode === "new") {
        const { data } = await api.post(`/projects/${projectId}/work-areas`, { ...payload, request_key: reqKeyRef.current });
        setRecords((prev) => {
          if (prev.some((r) => r.id === data.id)) return prev;
          return [data, ...prev];
        });
        toast.success(t("work_areas.toast.saved_draft"));
      } else {
        const wid = editing.rec.id;
        const { data } = await api.put(`/projects/${projectId}/work-areas/${wid}`, payload);
        setRecords((prev) => prev.map((r) => (r.id === wid ? data : r)));
        toast.success(t("work_areas.toast.saved_review"));
      }
      setEditing(null);
    } catch (err) {
      const code = err?.response?.data?.detail?.error;
      const msg = code ? t(`work_areas.errors.${code}`) : t("work_areas.errors.save_failed");
      toast.error(msg && !msg.startsWith("work_areas.errors.") ? msg : t("work_areas.errors.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  const approve = async (rec) => {
    if (approvingId) return;
    setApprovingId(rec.id);
    try {
      const { data } = await api.post(`/projects/${projectId}/work-areas/${rec.id}/approve`);
      setRecords((prev) => prev.map((r) => (r.id === rec.id ? data : r)));
      toast.success(t("work_areas.toast.approved"));
    } catch (err) {
      const code = err?.response?.data?.detail?.error;
      const msg = code ? t(`work_areas.errors.${code}`) : t("work_areas.errors.approve_failed");
      toast.error(msg && !msg.startsWith("work_areas.errors.") ? msg : t("work_areas.errors.approve_failed"));
    } finally {
      setApprovingId(null);
    }
  };

  const remove = async (rec) => {
    if (deletingId) return;
    if (!window.confirm(t("work_areas.actions.delete_confirm"))) return;
    setDeletingId(rec.id);
    try {
      await api.delete(`/projects/${projectId}/work-areas/${rec.id}`);
      setRecords((prev) => prev.filter((r) => r.id !== rec.id));
      toast.success(t("work_areas.toast.deleted"));
    } catch {
      toast.error(t("work_areas.errors.delete_failed"));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="jc-card p-5 mb-6" data-testid="work-areas-card">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="shrink-0 w-9 h-9 rounded-md bg-[var(--jc-blue)]/10 text-[var(--jc-blue)] flex items-center justify-center">
            <Ruler size={18} />
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg" data-testid="work-areas-title">{t("work_areas.card_title")}</div>
            <div className="text-xs text-[var(--jc-text-soft)]">{t("work_areas.card_subtitle")}</div>
            {records.length > 0 && (() => {
              const approvedCount = records.filter((r) => r && r.approval_status === "approved").length;
              const reviewCount   = records.filter((r) => r && r.approval_status !== "approved").length;
              return (
                <div
                  className="mt-1.5 text-xs flex flex-wrap items-center gap-x-2 gap-y-1"
                  data-testid="work-areas-review-summary"
                >
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 px-2 py-0.5 border border-emerald-200"
                    data-testid="work-areas-review-summary-approved"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" aria-hidden="true" />
                    {t("work_areas.review_summary.approved_count", approvedCount)}
                  </span>
                  {reviewCount > 0 && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 px-2 py-0.5 border border-amber-200"
                      data-testid="work-areas-review-summary-review-required"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" aria-hidden="true" />
                      {t("work_areas.review_summary.review_required_count", reviewCount)}
                    </span>
                  )}
                  {approvedCount > 0 && (
                    <span className="text-[var(--jc-text-soft)]" data-testid="work-areas-review-summary-hint">
                      {t("work_areas.review_summary.ready_hint")}
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
        <button
          type="button"
          onClick={startNew}
          disabled={Boolean(editing) || choosing}
          data-testid="work-areas-measure-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60 shrink-0"
        >
          <Plus size={14} /> {t("work_areas.measure_action")}
        </button>
      </div>

      {/* Slice VI-02 · Levi Candidate Review — additive section that
          surfaces preliminary VI-01 measurement candidates inside
          this same card. Reinforces the video-first hierarchy without
          blocking the manual workflow. */}
      <LeviCandidateReviewSection projectId={projectId} onPromotedChange={load} />

      {choosing && (
        <div className="mt-3 rounded-md border border-[var(--jc-border-strong)] p-4" data-testid="work-areas-chooser">
          <div className="font-display text-lg mb-3" data-testid="work-areas-chooser-title">
            {t("work_areas.chooser_title")}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {["interior_room","exterior_area","roof_section","elevation","object","linear_measurement","something_else"].map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => pickChooser(k)}
                data-testid={`work-areas-chooser-${k.replace(/_/g,"-")}`}
                className="jc-card p-3 text-left text-sm font-semibold hover:border-[var(--jc-blue)] hover:bg-[var(--jc-bg)]"
              >
                {t(`work_areas.chooser.${k}`)}
              </button>
            ))}
          </div>
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={cancelChoosing}
              data-testid="work-areas-chooser-cancel-btn"
              className="text-sm font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]"
            >
              {t("work_areas.chooser_cancel")}
            </button>
          </div>
        </div>
      )}

      {editing && (
        <div className="mt-3 rounded-md border border-[var(--jc-border-strong)] p-3" data-testid="work-area-editor-wrapper">
          <WorkAreaEditor
            t={t}
            initial={editing.draft}
            onCancel={cancelEditing}
            onSave={save}
            saving={saving}
            mode={editing.mode}
            onChangeType={editing.mode === "new" ? () => { setEditing(null); setChoosing(true); } : undefined}
          />
        </div>
      )}

      {!editing && !choosing && !loading && records.length === 0 && (
        <div data-testid="work-areas-empty" className="rounded-md border border-dashed border-[var(--jc-border)] p-4 text-sm text-[var(--jc-text-soft)] space-y-2">
          <div>{t("work_areas.empty_state")}</div>
          <div data-testid="work-areas-empty-optional" className="text-xs">{t("work_areas.empty_state_optional")}</div>
        </div>
      )}

      {!editing && !choosing && records.length > 0 && (
        <div className="space-y-3 mt-3" data-testid="work-areas-list">
          {records.map((r) => (
            <WorkAreaReviewCard
              key={r.id}
              t={t}
              rec={r}
              onEdit={startEdit}
              onApprove={approve}
              onDelete={remove}
              approving={approvingId === r.id}
              deleting={deletingId === r.id}
            />
          ))}
          <div className="text-xs text-[var(--jc-text-soft)] italic" data-testid="work-areas-ready-hint">
            {t("work_areas.saved_ready_hint")}
          </div>
        </div>
      )}

      <div className="mt-3 text-xs text-[var(--jc-text-soft)]" data-testid="work-areas-future-methods-note">
        {t("work_areas.future_methods_note")}
      </div>
    </div>
  );
};

export default WorkAreas;
